import { Injectable, signal } from '@angular/core';

export type ToastType = 'info' | 'success' | 'warning' | 'error';
export type ToastActionVariant = 'primary' | 'secondary';

export interface ToastAction {
  label: string;
  variant?: ToastActionVariant;
  closeOnClick?: boolean;
  callback: () => void;
}

export interface AppToast {
  id: number;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  actions?: ToastAction[];
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastId = 0;

  readonly toasts = signal<AppToast[]>([]);

  show(
    type: ToastType,
    title: string,
    message?: string,
    duration = 5000,
    actions: ToastAction[] = []
  ): void {
    const toast: AppToast = {
      id: ++this.toastId,
      type,
      title,
      message,
      duration,
      actions,
    };

    this.toasts.update((items) => [...items, toast].slice(-3));

    if (duration > 0) {
      window.setTimeout(() => {
        this.remove(toast.id);
      }, duration);
    }
  }

  info(
    title: string,
    message?: string,
    duration = 5000,
    actions: ToastAction[] = []
  ): void {
    this.show('info', title, message, duration, actions);
  }

  success(
    title: string,
    message?: string,
    duration = 4000,
    actions: ToastAction[] = []
  ): void {
    this.show('success', title, message, duration, actions);
  }

  warning(
    title: string,
    message?: string,
    duration = 6000,
    actions: ToastAction[] = []
  ): void {
    this.show('warning', title, message, duration, actions);
  }

  error(
    title: string,
    message?: string,
    duration = 0,
    actions: ToastAction[] = []
  ): void {
    this.show('error', title, message, duration, actions);
  }

  remove(id: number): void {
    this.toasts.update((items) => items.filter((toast) => toast.id !== id));
  }

  clear(): void {
    this.toasts.set([]);
  }
}